import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PDFDownloadLink } from '@react-pdf/renderer';

import Loading from '../utils/Loading';
import PayloadMap from '../payload/PayloadMap';
import StatusDrawer from './StatusDrawer';
import HistoryDrawer from '../historyDrawer/HistoryDrawer';
import IdChip from './IdChip';
import PayloadContentQA from './PayloadContentQA';
import LocationInfoCard from './LocationInfoCard';
import PayloadScoreCard, { deriveScoreGroups } from './PayloadScoreCard';
import PayloadInsightsCard, { deriveInsightCounts } from './PayloadInsightsCard';
import GeppDataSheet from '../data_sheet/gepp/DataSheet';
import { Icons } from './Icons';
import { Tour, useTour } from '../tour';
import getPayloadNewSteps from '../tour/steps/payloadNewSteps';

import { get_payload_info, get_payload_history, set_payload_images } from '../../actions/payloadActions';
import { get_screens_data } from '../../actions/organizationActions';
import { get_indicators_progress, get_payload_insights } from '../../actions/indicatorsActions';
import { get_location_data, get_locations } from '../../actions/locationActions';
import { get_org_reports } from '../../actions/reportsActions';
import { search_payload_fraud } from '../../actions/fraudActions';
import { get_ticket_reasons, get_ticket_settings, get_my_tickets, get_resolved_tickets, get_ticket_exists, get_ticket_history, reasonsForTarget, ticketsForTarget, existsForTarget, historyForTicket, pickStatusNote } from '../../actions/ticketActions';
import ReportErrorModal from '../displayNew/ReportErrorModal';
import ReportBanner, { pickLatestTicket } from '../displayNew/ReportBanner';

import parsePayloadData from '../../utils/parsePayloadData';
import { getPayloadStatusDesc, getTicketStatusDesc } from '../../utils/getStatusDesc';
import { epochToLocalDate } from '../../utils/dateConverter';
import isEmpty from '../../utils/isEmpty';

import './payloadNew.css';

const SECTIONS = [
	{ id: 'identity', labelKey: 'payload.rail.store',    icon: 'store' },
	{ id: 'payload',  labelKey: 'payload.rail.details',  icon: 'phone' },
	{ id: 'status',   labelKey: 'payload.rail.status',   icon: 'status' },
	{ id: 'location', labelKey: 'payload.rail.geo',      icon: 'map' },
	{ id: 'score',    labelKey: 'payload.rail.score',    icon: 'award' },
	{ id: 'insights', labelKey: 'payload.rail.insights', icon: 'bulb' },
	{ id: 'content',  labelKey: 'payload.rail.content',  icon: 'grid' }
];

// Maps PAYLOAD_STATUS_* codes (0..11) to icon + tone. See utils/getStatusDesc.js.
const STATUS_META = {
	0:  { icon: 'dash',   tone: 'neutral' }, // none
	1:  { icon: 'plus',   tone: 'neutral' }, // created
	2:  { icon: 'clock',  tone: 'warn' },    // waiting
	3:  { icon: 'loader', tone: 'info' },    // processing
	4:  { icon: 'check',  tone: 'pass' },    // good
	5:  { icon: 'alert',  tone: 'warn' },    // incomplete
	6:  { icon: 'cross',  tone: 'fail' },    // bad
	7:  { icon: 'alert',  tone: 'warn' },    // flagged
	8:  { icon: 'search', tone: 'info' },    // in review
	9:  { icon: 'check',  tone: 'pass' },    // passed
	10: { icon: 'cross',  tone: 'fail' },    // removed
	11: { icon: 'cross',  tone: 'fail' },    // error
};

const PayloadNew = () => {
	const { t } = useTranslation();
	const { location_id, payload_id } = useParams();
	const dispatch = useDispatch();
	const tour = useTour({ storageKey: 'payload-new-tour-v1', autoStart: true });
	const tourSteps = useMemo(() => getPayloadNewSteps(t), [t]);

	const { payload_loading, payload_info, payload_history, payload_images } = useSelector((s) => s.payload);
	const { screens_data } = useSelector((s) => s.organization);
	const { indicators_progress, payload_insights, indicators_errors } = useSelector((s) => s.indicators);
	const { locations, location_data } = useSelector((s) => s.location);
	const { org_reports } = useSelector((s) => s.reports);
	const { ticket_reasons, ticket_settings, my_tickets, resolved_tickets, ticket_exists, ticket_history } = useSelector((s) => s.ticket);
	// Both lookups are filtered to tickets that claim the payload itself: the
	// endpoint also returns the tickets of this payload's images, and counting
	// those here would let a photo's ticket drive the payload banner. The
	// server `count` includes them too, so we measure the filtered array.
	// The whole document is kept, not just its status: the QA note is fetched by
	// the ticket's own id.
	const latestResolvedTicket = useMemo(
		() => pickLatestTicket(ticketsForTarget(resolved_tickets?.tickets, 'payload')),
		[resolved_tickets]
	);
	const latestResolvedStatus = latestResolvedTicket?.status ?? null;
	const resolvedTicketId = latestResolvedTicket?._id?.$oid ?? null;
	// What QA wrote when it moved the ticket into that state, shown under the
	// generic copy in the banner. Null whenever there is nothing to show — the
	// common case, since the comment is optional across the whole API.
	const resolvedNote = useMemo(
		() => pickStatusNote(historyForTicket(ticket_history, resolvedTicketId), latestResolvedStatus),
		[ticket_history, resolvedTicketId, latestResolvedStatus]
	);
	// "Already reported" must answer "does this object have a live ticket?", not
	// "do I have one?". /mine only returns what the token's user filed, so a
	// ticket raised by a colleague left the CTA visible and the create then
	// answered 208. /exists answers the right question and always agrees with
	// the create. When it can't be trusted (not fetched yet, failed, or left
	// over from another object) fall back to the /mine-derived value.
	const existsAnswer = useMemo(
		() => existsForTarget(ticket_exists, 'payload', payload_id),
		[ticket_exists, payload_id]
	);
	const hasActiveTicket = useMemo(
		() => (existsAnswer !== null
			? existsAnswer
			: ticketsForTarget(my_tickets?.tickets, 'payload').length > 0),
		[existsAnswer, my_tickets]
	);
	// The ticket the history button opens: the live one while it is open, the
	// resolved one afterwards — the same precedence the banner itself renders.
	// A live ticket filed by somebody else is deliberately absent: /exists says
	// the object is blocked but not by which ticket, so there is no id to fetch a
	// log with and the button stays hidden.
	const liveTicket = useMemo(
		() => pickLatestTicket(ticketsForTarget(my_tickets?.tickets, 'payload')),
		[my_tickets]
	);
	const trackedTicket = hasActiveTicket ? liveTicket : latestResolvedTicket;
	const trackedTicketId = trackedTicket?._id?.$oid ?? null;
	const ticketHistory = useMemo(
		() => historyForTicket(ticket_history, trackedTicketId),
		[ticket_history, trackedTicketId]
	);
	// Only reasons whose ticket_types bitmask includes `payload` are offered
	// here; image-only reasons stay on the Display page.
	const payloadReasonsCount = useMemo(
		() => reasonsForTarget(ticket_reasons?.reasons, 'payload').length,
		[ticket_reasons]
	);
	// Report window in days from the payload's date; 0 means "no window".
	// When expired we swap the button for an inline notice instead of letting
	// the backend answer with a 400.
	const reportWindow = useMemo(() => {
		const cfg = ticket_settings || {};
		const days = Number.isFinite(cfg.window_days) ? cfg.window_days : 30;
		const epoch = payload_info?.date?.$date;
		if (!days || !epoch) return { days, expired: false };
		const ageDays = (Date.now() - epoch) / 86400000;
		return { days, expired: ageDays > days };
	}, [ticket_settings, payload_info]);

	const [locationInfo, setLocationInfo] = useState(null);
	const [parsedPayload, setParsedPayload] = useState([]);
	const [drawer, setDrawer] = useState(false);
	const [activeSection, setActiveSection] = useState('identity');
	const [pdfTimer, setPdfTimer] = useState(0);
	const [pdfDisabled, setPdfDisabled] = useState(true);
	const [urlCopied, setUrlCopied] = useState(false);
	const [reportOpen, setReportOpen] = useState(false);
	const [ticketDrawer, setTicketDrawer] = useState(false);

	// A ticket that has only just been opened has a single transition, and a log
	// of one row says nothing the banner does not already say. The button waits
	// until there is a path to show.
	const openTicketHistory = ticketHistory.length > 1
		? () => setTicketDrawer(true)
		: null;

	// Live tickets on this payload (OPEN=1 / REVIEW=2). If count>0 the report
	// banner shows the "already reported" notice instead of the CTA so the
	// user can't fire a duplicate.
	// Scope of the lookups below. A ticket is stored under the organization of
	// the object it claims, not the author's, so a Percepthor user reviewing a
	// client's payload has to name that organization or /mine falls back to the
	// token's own and answers empty. Only a platform admin may name a foreign
	// one; naming your own is always accepted.
	const ticketOrgId = payload_info?.organization?._id?.$oid;

	useEffect(() => {
		if (!payload_id) return;
		// Is the object blocked, whoever filed it — drives the banner. Takes no
		// organization: the scope comes from the claimed object itself.
		dispatch(get_ticket_exists('payload', payload_id));
	}, [payload_id]);

	// These wait for the organization. Nothing is lost by waiting: the banner is
	// gated on the reasons catalogue, which needs the same id.
	useEffect(() => {
		if (!payload_id || !ticketOrgId) return;
		dispatch(get_my_tickets({ payload: payload_id, organization: ticketOrgId, multiple: '1,2' }));
		// Resolved tickets (APPROVED..ERROR) so the banner can surface the
		// outcome of the last report instead of silently going back to the CTA.
		dispatch(get_resolved_tickets({ payload: payload_id, organization: ticketOrgId, multiple: '3,4,5,6,7,8' }));
	}, [payload_id, ticketOrgId]);

	// Transition log of the resolved ticket, for the QA note in the banner. Only
	// fires once there IS a resolved ticket: with none there is nothing to ask
	// for. Takes no organization — the scope comes from the ticket itself.
	useEffect(() => {
		if (trackedTicketId) dispatch(get_ticket_history(trackedTicketId));
	}, [trackedTicketId]);

	// The reasons catalogue still needs the org id, so it waits for payload_info.
	useEffect(() => {
		if (ticketOrgId) dispatch(get_ticket_reasons(ticketOrgId));
	}, [ticketOrgId]);

	// Effective ticket config (description bounds, report window), scoped by the
	// token. Passing the payload's module lets a module-level document override
	// the organization one; without it the org config is resolved instead.
	useEffect(() => {
		dispatch(get_ticket_settings(payload_info?.module?._id?.$oid));
	}, [payload_info?.module?._id?.$oid]);

	useEffect(() => {
		dispatch(get_payload_info(payload_id));
		dispatch(get_payload_history(payload_id));
		dispatch(search_payload_fraud({ payload: payload_id }));
		dispatch(get_payload_insights(payload_id));
	}, []);

	useEffect(() => {
		if (!isEmpty(payload_info)) {
			dispatch(get_screens_data(payload_info?.submodule?._id?.$oid));

			if (payload_info.location) {
				setLocationInfo(payload_info.location);
			} else {
				dispatch(get_locations({
					organization: payload_info?.organization?._id?.$oid,
					code: payload_info?.data?.code
				}));
			}

			dispatch(get_indicators_progress({
				location: payload_info?.location?._id?.$oid,
				route: payload_info?.route?._id?.$oid,
				cycle: payload_info?.cycle?._id?.$oid,
				payload: payload_id
			}));

			dispatch(get_org_reports({
				organization: payload_info?.organization?._id?.$oid,
				app: process.env.REACT_APP_ALIAS
			}));
		}
	}, [payload_info]);

	useEffect(() => {
		if (locations?.count > 0) {
			const mostRecent = locations.locations.reduce((prev, cur) => {
				return new Date(cur.date) > new Date(prev.date) ? cur : prev;
			});
			setLocationInfo(mostRecent);
		}
	}, [locations]);

	useEffect(() => {
		if (!isEmpty(locationInfo)) {
			const locId = locationInfo?._id?.$oid || locationInfo?._id;
			dispatch(get_location_data(locId, { workspace: payload_info?.workspace?._id?.$oid }));
		}
	}, [locationInfo]);

	useEffect(() => {
		if (screens_data && payload_info?.data) {
			setParsedPayload(parsePayloadData(screens_data, payload_info.data));
			dispatch(set_payload_images(payload_info.data));
		}
	}, [screens_data, payload_info]);

	useEffect(() => {
		if (!Array.isArray(payload_images) || payload_images.length === 0) return;
		if (pdfTimer < payload_images.length) {
			const id = setInterval(() => setPdfTimer((prev) => prev + 1), 1000);
			return () => clearInterval(id);
		}
		setPdfDisabled(false);
	}, [pdfTimer, payload_images]);

	const historyDate = useMemo(() => {
		if (!Array.isArray(payload_history) || payload_history.length === 0) return '';
		const last = payload_history[payload_history.length - 1];
		return last?.date?.$date
			? epochToLocalDate(last.date.$date, localStorage.getItem('i18nextLng'))
			: '';
	}, [payload_history]);

	const createdDate = useMemo(() => {
		const d = payload_info?.date?.$date;
		return d ? epochToLocalDate(d, localStorage.getItem('i18nextLng')) : '';
	}, [payload_info]);

	const statusLabel = useMemo(() => getPayloadStatusDesc(payload_info?.status), [payload_info]);
	const statusCode = parseInt(payload_info?.status);
	const statusMeta = STATUS_META[statusCode] || { icon: 'check', tone: 'neutral' };
	const StatusIcon = Icons[statusMeta.icon] || Icons.check;
	const statusIsLoading = statusCode === 3;

	// Rail filter needs to know whether the score section will render; the card
	// re-derives groups internally but we keep this gate here as a lightweight
	// presence check (deriveScoreGroups is exported by the card module so the
	// two sides stay in sync without duplicating the mapping logic).
	const hasScore = useMemo(() => (
		deriveScoreGroups(indicators_progress?.indicators).length > 0
		&& !indicators_errors?.indicators_progress
	), [indicators_progress, indicators_errors]);

	const insightCounts = useMemo(
		() => deriveInsightCounts(payload_insights?.results),
		[payload_insights]
	);

	const hasInsights = insightCounts.total > 0 && !indicators_errors?.payload_insights;

	if (payload_loading || isEmpty(payload_info)) {
		return <Loading msg={t('payload.loading')} />;
	}

	const distanceM = (() => {
		const lat1 = locationInfo?.lat;
		const lon1 = locationInfo?.lon;
		const lat2 = payload_info?.props?.user_lat;
		const lon2 = payload_info?.props?.user_lon;
		if (!lat1 || !lon1 || !lat2 || !lon2) return null;
		const R = 6371e3;
		const toRad = (v) => (v * Math.PI) / 180;
		const dLat = toRad(lat2 - lat1);
		const dLon = toRad(lon2 - lon1);
		const a = Math.sin(dLat / 2) ** 2
			+ Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
		const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
		return Math.round(R * c);
	})();

	const detailsHref = location_id
		? `/locations/${location_id}/payloads/${payload_id}/insights`
		: null;

	const renderRailIcon = (id) => {
		const map = { identity: 'store', payload: 'phone', status: 'status', score: 'award', insights: 'bulb', location: 'map', content: 'grid' };
		const Icon = Icons[map[id]];
		return Icon ? <Icon /> : null;
	};

	const handleCopyUrl = async () => {
		// Share a clean, canonical link to the payload — drop the query params
		// (`?token=...`, search state, etc.) and the rail-anchor hash
		// (`#pn-score`, `#pn-identity`, ...) so the recipient lands on the
		// page itself and not on whichever section the sharer was scrolled to.
		const url = window.location.origin + window.location.pathname;
		let ok = false;
		if (navigator.clipboard && window.isSecureContext) {
			try { await navigator.clipboard.writeText(url); ok = true; }
			catch (e) { ok = false; }
		}
		if (!ok) {
			const ta = document.createElement('textarea');
			ta.value = url;
			ta.setAttribute('readonly', '');
			ta.style.position = 'fixed';
			ta.style.opacity = '0';
			ta.style.left = '-9999px';
			document.body.appendChild(ta);
			ta.focus();
			ta.select();
			try { ok = document.execCommand('copy'); } catch (e) { ok = false; }
			document.body.removeChild(ta);
		}
		if (ok) {
			setUrlCopied(true);
			setTimeout(() => setUrlCopied(false), 1500);
		}
	};

	const pdfReportName = org_reports?.[0]?.name;
	const pdfFileName = `FichaTecnica-${locationInfo?.code}-${locationInfo?.name}.pdf`;

	const renderPdfButton = (variant) => {
		if (!pdfReportName) return null;
		return (
			<PDFDownloadLink
				document={
					<GeppDataSheet
						title={pdfReportName}
						locationInfo={locationInfo}
						locationData={location_data}
						payloadInfo={payload_info}
						indicatorsProgress={indicators_progress?.indicators}
						parsedPayload={parsedPayload}
					/>
				}
				fileName={pdfFileName}
				className={`pn-pdf-link pn-pdf-link--${variant}`}
			>
				{({ loading }) => {
					const busy = pdfDisabled || loading;
					return (
						<button type='button' className={`pn-pdf-btn ${busy ? 'is-busy' : ''}`} disabled={busy}>
							<i className='bi bi-file-earmark-pdf-fill'></i>
							<span>{busy ? `Generando ${pdfReportName}` : `Descargar ${pdfReportName}`}</span>
						</button>
					);
				}}
			</PDFDownloadLink>
		);
	};

	return (
		<div id={localStorage.getItem('theme')}>
			<div className='payload-new'>
				<div className='pn-layout'>
					<aside className='pn-rail'>
						<div className='pn-rail-title'>{t('payload.rail.title', { defaultValue: 'On this page' })}</div>
						<nav className='pn-rail-nav'>
							{SECTIONS
								.filter((s) => (s.id === 'score' ? hasScore : s.id === 'insights' ? hasInsights : true))
								.map((s) => (
									<a
										key={s.id}
										href={`#pn-${s.id}`}
										className={`pn-rail-link ${activeSection === s.id ? 'active' : ''}`}
										onClick={() => setActiveSection(s.id)}
									>
										<span className='pn-rail-icon'>{renderRailIcon(s.id)}</span>
										{t(s.labelKey)}
									</a>
								))
							}
						</nav>

						{renderPdfButton('rail')}

						<div className='pn-rail-share'>
							{t('payload.share.prompt', { defaultValue: 'Need to share this payload?' })}{' '}
							<button type='button' className='pn-rail-share-btn' onClick={handleCopyUrl}>
								{urlCopied
									? t('payload.share.copied', { defaultValue: 'Copied!' })
									: t('payload.share.copyUrl', { defaultValue: 'Copy URL' })}
							</button>
						</div>

						<button type='button' className='ds-tour-launcher' onClick={tour.start}>
							<Icons.compass /> {t('tour.launcher', { defaultValue: 'Take a tour' })}
						</button>
					</aside>

					<div className='pn-main'>
						{/* Identity hero */}
						<LocationInfoCard
							sectionId='pn-identity'
							locationInfo={locationInfo}
							actions={
								<>
									<span className='pn-payload-pill'>
										<span className={`pn-payload-pill-mode ${payload_info?.process_type === 3 ? 'offline' : 'online'}`}>
											{payload_info?.process_type === 3 ? <Icons.wifiOff /> : <Icons.wifi />}
											{payload_info?.process_type === 3
												? t('payload.payloadInfo.processType.offline')
												: t('payload.payloadInfo.processType.online')}
										</span>
									</span>
									{renderPdfButton('hero')}
								</>
							}
						/>

						{/* Payload + Status */}
						<div className='pn-row-2'>
							<section id='pn-payload' className='pn-card'>
								<div className='pn-section-header'>
									<h2 className='pn-section-title'><Icons.phone /> {t('payload.payloadInfo.title')}</h2>
									<IdChip label='Id' value={payload_info?._id?.$oid} full />
								</div>
								<div className='pn-stat-grid cols-3'>
									<Stat label={t('payload.payloadInfo.workspace')} value={payload_info?.workspace?.name} />
									<Stat label={t('payload.payloadInfo.module')} value={payload_info?.module?.name} />
									<Stat label={t('payload.payloadInfo.submodule')} value={payload_info?.submodule?.title} />
									<Stat label={t('payload.payloadInfo.config')} value={payload_info?.configuration?.version} mono />
									<Stat label={t('payload.payloadInfo.user')} value={payload_info?.user?.username} mono />
									<Stat label={t('payload.payloadInfo.cycle')} value={payload_info?.cycle?.name} />
									{payload_info?.region?.name && (
										<Stat label={t('payload.payloadInfo.region')} value={payload_info.region.name} />
									)}
									{payload_info?.zone?.name && (
										<Stat label={t('payload.payloadInfo.zone')} value={payload_info.zone.name} />
									)}
									{payload_info?.depot?.name && (
										<Stat label={t('payload.payloadInfo.depot')} value={payload_info.depot.name} />
									)}
									{payload_info?.group?.name && (
										<Stat label={t('payload.payloadInfo.group')} value={payload_info.group.name} />
									)}
									<Stat label={t('payload.payloadInfo.route')} value={payload_info?.route?.code} mono />
									<Stat label={t('payload.payloadInfo.creationDate')} value={createdDate} mono />
								</div>
							</section>

							<section id='pn-status' className='pn-card pn-status-card'>
								<h2 className='pn-section-title'><Icons.status /> {t('payload.payloadStatus.title')}</h2>
								<div className='pn-status-row'>
									<div className={`pn-status-icon pn-status-icon--${statusMeta.tone}${statusIsLoading ? ' pn-status-icon--spin' : ''}`}>
										<StatusIcon />
									</div>
									<div>
										<div className='pn-status-state'>{statusLabel}</div>
										<div className='pn-status-date'>{historyDate}</div>
									</div>
								</div>
								{Array.isArray(payload_history) && payload_history.length > 0 && (
									<div className='pn-status-mini'>
										{payload_history.map((_h, i) => (
											<React.Fragment key={i}>
												<span className={`pn-status-mini-dot ${i === payload_history.length - 1 ? 'is-last' : ''}`} />
												{i < payload_history.length - 1 && <span className='pn-status-mini-line' />}
											</React.Fragment>
										))}
									</div>
								)}
								<button type='button' className='pn-btn-ghost' onClick={() => setDrawer(true)}>
									<Icons.history /> {t('payload.payloadStatus.historyBtn')}
								</button>
							</section>
						</div>

						{/* Payload Location */}
						<section id='pn-location' className='pn-card pn-card--flush'>
							<div className='pn-location'>
								<div className='pn-location-map'>
									{locationInfo?.lat && locationInfo?.lon && payload_info?.props?.user_lat && payload_info?.props?.user_lon ? (
										<PayloadMap
											locationLat={locationInfo.lat}
											locationLng={locationInfo.lon}
											userLat={payload_info.props.user_lat}
											userLng={payload_info.props.user_lon}
										/>
									) : (
										<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--ds-muted)', fontSize: 13 }}>
											No location data available
										</div>
									)}
								</div>
								<aside className='pn-location-aside'>
									<h2 className='pn-section-title'><Icons.map /> {t('payload.payloadLocation.title')}</h2>
									{distanceM !== null && (
										<div className='pn-distance-card'>
											<div className='pn-stat-label'>{t('payload.payloadLocation.distance', { defaultValue: 'Distance' })}</div>
											<div className={`pn-distance-value ${distanceM <= 30 ? 'near' : 'far'}`}>
												{distanceM >= 1000
													? (distanceM / 1000).toLocaleString(localStorage.getItem('i18nextLng'), { maximumFractionDigits: 2 })
													: distanceM}
												<span className='pn-distance-unit'>{distanceM >= 1000 ? t('payload.payloadLocation.unitKm', { defaultValue: 'km' }) : t('payload.payloadLocation.unitM', { defaultValue: 'm' })}</span>
											</div>
											<div className='pn-distance-sub'>{t('payload.payloadLocation.between', { defaultValue: 'between user and store' })}</div>
										</div>
									)}
									<Stat label='User lat' value={payload_info?.props?.user_lat} mono />
									<Stat label='User lon' value={payload_info?.props?.user_lon} mono />
									<Stat label='Store id' value={locationInfo?._id?.$oid ?? locationInfo?._id} mono />
									<div className='pn-legend-row'>
										<span><span className='pn-legend-dot' style={{ background: '#ef4444' }} /> Store</span>
										<span><span className='pn-legend-dot' style={{ background: '#3b82f6' }} /> User</span>
									</div>
								</aside>
							</div>
						</section>

						{/* Report error banner — sits right above the score card since
						    a wrong score is the main thing users report from here.
						    Same three states as Display: active ticket → info
						    notice, window expired → muted notice, otherwise the CTA.
						    Only payload-applicable reasons count towards showing it. */}
						{payloadReasonsCount > 0 && (
							<ReportBanner
								prefix='pn'
								ns='payload'
								hasActiveTicket={hasActiveTicket}
								resolvedStatus={latestResolvedStatus}
								note={resolvedNote}
								onHistory={openTicketHistory}
								expired={reportWindow.expired}
								days={reportWindow.days}
								onReport={() => setReportOpen(true)}
							/>
						)}

						{/* Score */}
						<PayloadScoreCard
							sectionId='pn-score'
							indicators={indicators_progress?.indicators}
							errored={!!indicators_errors?.indicators_progress}
						/>

						{/* Insights */}
						{hasInsights && (
							<PayloadInsightsCard
								sectionId='pn-insights'
								counts={insightCounts}
								detailsHref={detailsHref}
							/>
						)}

						{/* Content */}
						<section id='pn-content' className='pn-card'>
							<div className='pn-section-header'>
								<h2 className='pn-section-title'><Icons.grid /> {t('payload.payloadContent.title')}</h2>
							</div>
							<PayloadContentQA content={parsedPayload} />
						</section>

						<div className='pn-mobile-footer'>
							<div className='pn-rail-share'>
								{t('payload.share.prompt', { defaultValue: 'Need to share this payload?' })}{' '}
								<button type='button' className='pn-rail-share-btn' onClick={handleCopyUrl}>
									{urlCopied
										? t('payload.share.copied', { defaultValue: 'Copied!' })
										: t('payload.share.copyUrl', { defaultValue: 'Copy URL' })}
								</button>
							</div>
							<button type='button' className='ds-tour-launcher' onClick={tour.start}>
								<Icons.compass /> {t('tour.launcher', { defaultValue: 'Take a tour' })}
							</button>
						</div>
					</div>
				</div>
			</div>

			<ReportErrorModal
				open={reportOpen}
				onClose={() => setReportOpen(false)}
				payloadId={payload_id}
				organization={ticketOrgId}
			/>

			{/* Ticket transition log. Credits the note to Backoffice only on the rows
			    that carry one: the OPEN row is the reporter's own action, and the entry
			    never names the actor, so labelling every row would tell the client that
			    Backoffice filed their report. */}
			<HistoryDrawer
				open={ticketDrawer}
				onClose={() => setTicketDrawer(false)}
				history={ticketHistory}
				statusDesc={getTicketStatusDesc}
				eyebrow={trackedTicket?.code
					? t('display.reportError.banner.history.eyebrow', { code: trackedTicket.code })
					: t('display.reportError.banner.history.eyebrowFallback', { defaultValue: 'Ticket' })}
				title={t('display.reportError.banner.history.title', { defaultValue: 'Historial de estado' })}
				emptyText={t('display.reportError.banner.history.empty', { defaultValue: 'Sin historial disponible.' })}
				closeLabel={t('display.reportError.banner.history.close', { defaultValue: 'Cerrar historial' })}
				fromLabel=''
				author={(entry) => (entry?.comment
					? t('display.reportError.banner.history.author', { defaultValue: 'Backoffice' })
					: null)}
			/>

			<StatusDrawer
				open={drawer}
				onClose={() => setDrawer(false)}
				history={payload_history}
			/>

			<Tour
				steps={tourSteps}
				open={tour.isOpen}
				onClose={tour.stop}
			/>
		</div>
	);
};

const Stat = ({ label, value, mono }) => {
	const isMissing = value === undefined || value === null || value === '' || value === 'No information' || value === 'Invalid date';
	return (
		<div>
			<div className='pn-stat-label'>{label}</div>
			<div className={`pn-stat-value ${mono ? 'mono' : ''} ${isMissing ? 'muted' : ''}`}>
				{isMissing ? '—' : String(value)}
			</div>
		</div>
	);
};

export default PayloadNew;
